import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useMenuPermissions } from '../hooks/useMenuPermissions';
import { useI18n } from '../contexts/I18nContext';
import type { TranslationKey } from '../i18n/translations';
import { clientAPI } from '../services/api';
import {
  Home,
  Users,
  Building2,
  Calendar,
  Bell,
  CreditCard,
  LogOut,
  User,
  Menu,
  X,
  ChevronLeft,
  Search,
  Settings,
  Briefcase,
  Sparkles,
  Package,
  CheckCircle,
  TrendingUp,
  Clock,
  MessageCircle,
  MapPin,
  Star,
  Shield,
  HelpCircle,
  Headphones,
} from 'lucide-react';
import { getAppointments } from '../services/appointmentApi';
import { useKeyboardShortcuts, type KeyboardShortcut } from '../hooks/useKeyboardShortcuts';
import { useIsDesktop } from '../hooks/useMediaQuery';
import KeyboardShortcutsModal from './KeyboardShortcutsModal';
import { useBranding } from '../contexts/BrandingContext';
import { useCompanyFeatures } from '../contexts/CompanyFeaturesContext';

// Importar páginas
import Clientes from '../pages/Clientes';
import Propriedades from '../pages/Propriedades';
import Agendamentos from '../pages/Agendamentos';
import Funcionarios from '../pages/Funcionarios';
import Servicos from '../pages/Servicos';
import Produtos from '../pages/Produtos';
import Financeiro from '../pages/Financeiro';
import Relatorios from '../pages/Relatorios';
import Configuracoes from '../pages/Configuracoes';
import Zonas from '../pages/Zonas';
import Avaliacoes from '../pages/Avaliacoes';
import PreferenciasNotificacoes from '../pages/PreferenciasNotificacoes';
import ChatModal from './ChatModal';
import { UsersManagement } from '../pages/admin/Users';
import { CompaniesManagement } from '../pages/admin/Companies';
import MenuPermissions from '../pages/admin/MenuPermissions';
import RBACManagement from '../pages/admin/RBACManagement';
import CompanyLimitsManagement from '../pages/admin/CompanyLimitsManagement';
import { Billing } from '../pages/Billing';
import GlobalSearch from './GlobalSearch';
import OfflineStatusBanner from './OfflineStatusBanner';
import CompanySelector from './CompanySelector';
import AdminDashboard from './dashboards/AdminDashboard';
import EmployeeDashboard from './dashboards/EmployeeDashboard';
import ClientDashboard from './dashboards/ClientDashboard';
import Ajuda from '../pages/Ajuda';
import Suporte from '../pages/Suporte';
import {
  DEMO_MODE_STORAGE_KEY,
  DEMO_CLIENTS,
  DEMO_SIMPLE_STATS,
  DEMO_PROPERTIES_COUNT,
} from '../data/demoDashboardData';

interface Client {
  id: string;
  name: string;
  phone: string;
  document: string;
  created_at: string;
}

const SIDEBAR_WIDTH_PX = 250;

const PAGE_TITLES: Record<string, TranslationKey> = {
  dashboard: 'dashboard',
  usuarios: 'users',
  empresas: 'menu.companies',
  'permissoes-menu': 'menuPermissions',
  rbac: 'menu.rbac',
  limites: 'menu.limits',
  billing: 'billing.title',
  clientes: 'clients',
  funcionarios: 'employees',
  avaliacoes: 'menu.history',
  zonas: 'menu.zones',
  servicos: 'menu.services',
  produtos: 'products',
  propriedades: 'properties',
  agendamentos: 'appointments',
  financeiro: 'financial',
  relatorios: 'reports',
  configuracoes: 'settings',
  'preferencias-notificacoes': 'menu.notificationPreferences',
  suporte: 'support.title',
  ajuda: 'help',
};

const SimpleDashboard: React.FC = () => {
  const { user, logout, isAuthenticated, token } = useAuth();
  const { canAccess } = useMenuPermissions();
  const { t } = useI18n();
  const { branding } = useBranding();
  const { hasFeature } = useCompanyFeatures();
  const isDesktop = useIsDesktop();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const closeSidebar = useCallback(() => setSidebarOpen(false), []);

  useEffect(() => {
    if (!isDesktop && sidebarOpen) {
      document.body.classList.add('mobile-sidebar-open');
      return () => document.body.classList.remove('mobile-sidebar-open');
    }
    document.body.classList.remove('mobile-sidebar-open');
    return undefined;
  }, [isDesktop, sidebarOpen]);
  const [clients, setClients] = useState<Client[]>([]);
  const [activePage, setActivePage] = useState('dashboard');
  const [selectedClientForProperty, setSelectedClientForProperty] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [showShortcutsModal, setShowShortcutsModal] = useState(false);
  const searchOpenTriggerRef = useRef(0);
  const [demoModeEnabled, setDemoModeEnabled] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(DEMO_MODE_STORAGE_KEY) === 'true';
  });

  const brandPrimary = branding?.primaryColor ?? '#9333ea';
  const brandAccent = branding?.accentColor ?? '#3b82f6';
  const brandSecondary = branding?.secondaryColor ?? '#f0f0f0';
  const brandLogo = branding?.logoUrl;
  const canUseFinance = hasFeature('finance.module');
  const canUseReports = hasFeature('reports.module');
  const canUseNotificationHistory = hasFeature('notifications.history');
  const canUseNotificationPreferences = hasFeature('notifications.preferences');

  const loadClients = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await clientAPI.get('/clients', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setClients(response.data.clients || []);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
    }
  }, []);

  useEffect(() => {
    if (demoModeEnabled) {
      setClients(DEMO_CLIENTS.map(client => ({ ...client })));
    } else {
      loadClients();
    }
  }, [demoModeEnabled, loadClients]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(DEMO_MODE_STORAGE_KEY, demoModeEnabled ? 'true' : 'false');
    }
  }, [demoModeEnabled]);

  const handleMenuClick = (page: string) => {
    setActivePage(page);
    if (!isDesktop) {
      closeSidebar();
    }
  };

  const handleToggleDemoMode = () => {
    setDemoModeEnabled(prev => !prev);
  };

  const handleAddProperty = (clientId: string, clientName: string) => {
    setSelectedClientForProperty({ id: clientId, name: clientName });
    setActivePage('propriedades');
    if (!isDesktop) {
      closeSidebar();
    }
  };

  const handleSearchResultClick = (result: any) => {
    // Navegar para a página apropriada baseado no tipo de resultado
    switch (result.type) {
      case 'clients':
        setActivePage('clientes');
        break;
      case 'properties':
        setActivePage('propriedades');
        break;
      case 'appointments':
        setActivePage('agendamentos');
        break;
      case 'services':
        setActivePage('servicos');
        break;
      case 'products':
        setActivePage('produtos');
        break;
      case 'employees':
        setActivePage('funcionarios');
        break;
      default:
        break;
    }
    if (!isDesktop) {
      closeSidebar();
    }
  };

  const openChat = () => {
    setIsChatOpen(true);
  };

  const closeChat = () => {
    setIsChatOpen(false);
  };

  const [dashboardStats, setDashboardStats] = useState({
    totalAppointments: 0,
    completedToday: 0,
    completedThisMonth: 0,
    completionRate: 0,
    totalRevenue: 0,
    monthlyRevenue: 0,
    averageDuration: 0,
    timeDiscrepancies: {
      overTime: 0, // Serviços que excederam o tempo
      underTime: 0, // Serviços que foram mais rápidos
      exactTime: 0, // Serviços que foram exatamente no tempo
      averageDifference: 0, // Diferença média em minutos
    },
  });
  const [isLoadingStats, setIsLoadingStats] = useState(false);

  const loadDashboardStats = useCallback(async () => {
    setIsLoadingStats(true);
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);

      let appointmentsArray: any[] = [];
      try {
        const allAppointments = await getAppointments();
        if (allAppointments) {
          appointmentsArray = Array.isArray(allAppointments)
            ? allAppointments
            : (allAppointments?.appointments || allAppointments?.data || []);
        }
      } catch (error) {
        console.warn('Erro ao carregar agendamentos (não crítico):', error);
        appointmentsArray = [];
      }

      let completedArray: any[] = [];
      try {
        const completedAppointments = await getAppointments({ status: 'completed' });
        if (completedAppointments) {
          completedArray = Array.isArray(completedAppointments)
            ? completedAppointments
            : (completedAppointments?.appointments || completedAppointments?.data || []);
        }
      } catch (error) {
        console.warn('Erro ao carregar agendamentos completados (não crítico):', error);
        completedArray = [];
      }

      completedArray = completedArray.map((apt: any) => ({
        ...apt,
        actual_duration: apt.actual_duration || apt.ActualDuration || apt.actualDuration,
        end_time: apt.end_time || apt.EndTime || apt.endTime,
        duration: apt.duration || apt.Duration,
      }));

      const todayCompleted = completedArray.filter((apt: any) => {
        if (apt.status !== 'completed') return false;
        const aptDate = apt.end_time ? new Date(apt.end_time) : new Date(apt.scheduled_date);
        return aptDate >= today;
      });

      const monthCompleted = completedArray.filter((apt: any) => {
        if (apt.status !== 'completed') return false;
        const aptDate = apt.end_time ? new Date(apt.end_time) : new Date(apt.scheduled_date);
        return aptDate >= monthStart;
      });

      const totalRevenue = completedArray.reduce(
        (sum: number, apt: any) => sum + (apt.price || 0),
        0
      );
      const monthlyRevenue = monthCompleted.reduce(
        (sum: number, apt: any) => sum + (apt.price || 0),
        0
      );

      const completionRate =
        appointmentsArray.length > 0
          ? (completedArray.length / appointmentsArray.length) * 100
          : 0;

      const durations = completedArray
        .filter((apt: any) => apt.actual_duration || apt.duration)
        .map((apt: any) => apt.actual_duration || apt.duration);
      const avgDuration =
        durations.length > 0
          ? durations.reduce((sum: number, d: number) => sum + d, 0) / durations.length
          : 0;

      const timeDiscrepancies = {
        overTime: 0,
        underTime: 0,
        exactTime: 0,
        averageDifference: 0,
      };

      const differences: number[] = [];
      completedArray.forEach((apt: any) => {
        if (apt.actual_duration && apt.duration) {
          const diff = apt.actual_duration - apt.duration;
          differences.push(diff);
          if (diff > 0) {
            timeDiscrepancies.overTime++;
          } else if (diff < 0) {
            timeDiscrepancies.underTime++;
          } else {
            timeDiscrepancies.exactTime++;
          }
        }
      });

      timeDiscrepancies.averageDifference =
        differences.length > 0
          ? Math.round((differences.reduce((sum, d) => sum + d, 0) / differences.length) * 10) / 10
          : 0;

      setDashboardStats({
        totalAppointments: appointmentsArray.length,
        completedToday: todayCompleted.length,
        completedThisMonth: monthCompleted.length,
        completionRate: Math.round(completionRate * 10) / 10,
        totalRevenue,
        monthlyRevenue,
        averageDuration: Math.round(avgDuration),
        timeDiscrepancies,
      });
    } catch (error) {
      console.error('Erro ao carregar estatísticas do dashboard:', error);
    } finally {
      setIsLoadingStats(false);
    }
  }, []);

  useEffect(() => {
    if (demoModeEnabled) {
      setIsLoadingStats(false);
      setDashboardStats({
        ...DEMO_SIMPLE_STATS,
        timeDiscrepancies: { ...DEMO_SIMPLE_STATS.timeDiscrepancies },
      });
      return;
    }
    
    // Só carregar estatísticas se o usuário estiver autenticado e tiver token
    if (isAuthenticated && token) {
      // Adicionar delay para garantir que o token está totalmente propagado
      const timer = setTimeout(() => {
        const currentToken = localStorage.getItem('token');
        if (currentToken) {
          loadDashboardStats();
        } else {
          console.warn('[SimpleDashboard] Token não encontrado, pulando carregamento de estatísticas');
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [demoModeEnabled, loadDashboardStats, isAuthenticated, token]);

  // Configurar atalhos de teclado globais
  const globalShortcuts: KeyboardShortcut[] = [
    {
      key: 'k',
      ctrl: true,
      description: 'Abrir busca global',
      action: () => {
        searchOpenTriggerRef.current += 1;
      },
    },
    {
      key: '/',
      ctrl: true,
      description: 'Mostrar atalhos de teclado',
      action: () => {
        setShowShortcutsModal(true);
      },
    },
    {
      key: 'h',
      ctrl: true,
      description: 'Ir para Dashboard',
      action: () => {
        setActivePage('dashboard');
      },
    },
    {
      key: 'Escape',
      description: 'Fechar modais',
      action: () => {
        setIsChatOpen(false);
        setShowShortcutsModal(false);
      },
    },
  ];

  useKeyboardShortcuts(globalShortcuts, { ignoreInputs: true });

  const shouldShowDemoCta = !demoModeEnabled && dashboardStats.totalAppointments === 0 && clients.length === 0;

  const renderDashboard = () => {
    // Renderizar dashboard específico por role
    if (user?.role === 'admin') {
      return <AdminDashboard isDemoMode={demoModeEnabled} dashboardRoleKey="admin" />;
    }
    if (user?.role === 'supervisor') {
      return <AdminDashboard isDemoMode={demoModeEnabled} dashboardRoleKey="supervisor" />;
    } else if (user?.role === 'employee') {
      return <EmployeeDashboard isDemoMode={demoModeEnabled} />;
    } else if (user?.role === 'client') {
      return <ClientDashboard isDemoMode={demoModeEnabled} />;
    }

    // Dashboard padrão (fallback)
    return (
      <div style={{ padding: '30px' }}>
        <div style={{ marginBottom: '30px' }}>
          <h1 style={{ margin: '0 0 10px 0', color: '#333' }}>{t('dashboard')}</h1>
          <p style={{ color: '#666', margin: 0 }}>{t('dashboardSubtitle')}</p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginBottom: '20px' }}>
          {demoModeEnabled && (
            <DemoBanner
              variant="active"
              title="Modo demonstração ativado"
              description="Os indicadores abaixo estão exibindo números fictícios para facilitar apresentações e treinamentos."
              actionLabel="Desativar modo demo"
              onAction={handleToggleDemoMode}
            />
          )}
          {!demoModeEnabled && shouldShowDemoCta && (
            <DemoBanner
              variant="cta"
              title="Sem dados reais ainda?"
              description="Ative o modo demonstração para visualizar um painel completo com métricas e fluxos simulados."
              actionLabel="Ativar modo demo"
              onAction={handleToggleDemoMode}
            />
          )}
        </div>

        {/* Stats Cards */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '20px',
            marginBottom: '30px',
          }}
        >
          <StatCard icon={Users} label={t('clients')} value={clients.length} color={brandAccent} />
          <StatCard
            icon={Building2}
            label={t('properties')}
            value={demoModeEnabled ? DEMO_PROPERTIES_COUNT : 0}
            color="#10b981"
          />
          <StatCard
            icon={Calendar}
            label={t('appointments')}
            value={dashboardStats.totalAppointments}
            color="#f59e0b"
          />
          <StatCard
            icon={CreditCard}
            label={t('revenue')}
            value={`R$ ${dashboardStats.totalRevenue.toLocaleString('pt-BR')}`}
            color="#8b5cf6"
          />
        </div>

        {/* KPIs de Finalização */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '20px',
            marginBottom: '30px',
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              padding: '20px',
              borderLeft: '4px solid #10b981',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
              <CheckCircle style={{ fontSize: '24px', color: '#10b981', marginRight: '10px' }} />
              <h3 style={{ margin: 0, color: '#333', fontSize: '16px' }}>Completados Hoje</h3>
            </div>
            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#10b981' }}>
              {isLoadingStats ? '...' : dashboardStats.completedToday}
            </div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
              Serviços finalizados hoje
            </div>
          </div>

          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              padding: '20px',
              borderLeft: `4px solid ${brandAccent}`,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
              <Calendar style={{ fontSize: '24px', color: brandAccent, marginRight: '10px' }} />
              <h3 style={{ margin: 0, color: '#333', fontSize: '16px' }}>{t('dashboard.completedThisMonth')}</h3>
            </div>
            <div style={{ fontSize: '32px', fontWeight: 'bold', color: brandAccent }}>
              {isLoadingStats ? '...' : dashboardStats.completedThisMonth}
            </div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
              Serviços finalizados no mês
            </div>
          </div>

          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              padding: '20px',
              borderLeft: '4px solid #f59e0b',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
              <TrendingUp style={{ fontSize: '24px', color: '#f59e0b', marginRight: '10px' }} />
              <h3 style={{ margin: 0, color: '#333', fontSize: '16px' }}>{t('dashboard.completionRate')}</h3>
            </div>
            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#f59e0b' }}>
              {isLoadingStats ? '...' : `${dashboardStats.completionRate}%`}
            </div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
              {dashboardStats.totalAppointments > 0
                ? `${dashboardStats.completedThisMonth} de ${dashboardStats.totalAppointments} agendamentos`
                : 'Nenhum agendamento ainda'}
            </div>
          </div>

          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              padding: '20px',
              borderLeft: '4px solid #8b5cf6',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
              <CreditCard style={{ fontSize: '24px', color: '#8b5cf6', marginRight: '10px' }} />
              <h3 style={{ margin: 0, color: '#333', fontSize: '16px' }}>{t('reports.monthlyRevenue')}</h3>
            </div>
            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#8b5cf6' }}>
              {isLoadingStats ? '...' : `R$ ${dashboardStats.monthlyRevenue.toLocaleString('pt-BR')}`}
            </div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
              Receita dos serviços completados
            </div>
          </div>

          {/* KPI de Discrepâncias de Tempo */}
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              padding: '20px',
              borderLeft: '4px solid #dc2626',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
              <Clock style={{ fontSize: '24px', color: '#dc2626', marginRight: '10px' }} />
              <h3 style={{ margin: 0, color: '#333', fontSize: '16px' }}>{t('reports.timeDiscrepancies')}</h3>
            </div>
            <div
              style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc2626', marginBottom: '8px' }}
            >
              {isLoadingStats
                ? '...'
                : `${dashboardStats.timeDiscrepancies.overTime} serviços excederam`}
            </div>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>
              {dashboardStats.timeDiscrepancies.overTime > 0 && (
                <span style={{ color: '#dc2626' }}>
                  ⚠️ {dashboardStats.timeDiscrepancies.overTime} serviços demoraram mais que o
                  estimado
                </span>
              )}
              {dashboardStats.timeDiscrepancies.overTime === 0 &&
                dashboardStats.timeDiscrepancies.underTime > 0 && (
                  <span style={{ color: '#10b981' }}>
                    ✅ {dashboardStats.timeDiscrepancies.underTime} serviços foram mais rápidos
                  </span>
                )}
              {dashboardStats.timeDiscrepancies.overTime === 0 &&
                dashboardStats.timeDiscrepancies.underTime === 0 && (
                  <span style={{ color: '#666' }}>{t('dashboard.noDiscrepancy')}</span>
                )}
            </div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              Diferença média: {dashboardStats.timeDiscrepancies.averageDifference > 0 ? '+' : ''}
              {dashboardStats.timeDiscrepancies.averageDifference} min
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            padding: '20px',
          }}
        >
          <h3 style={{ margin: '0 0 20px 0', color: '#333' }}>{t('quickActions')}</h3>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '15px',
            }}
          >
            <button
              onClick={() => handleMenuClick('clientes')}
              style={{
                padding: '15px',
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '10px',
                transition: 'all 0.3s ease',
              }}
              onMouseOver={e => {
                e.currentTarget.style.backgroundColor = '#e9ecef';
              }}
              onMouseOut={e => {
                e.currentTarget.style.backgroundColor = '#f8f9fa';
              }}
            >
              <Users style={{ fontSize: '24px', color: brandAccent }} />
              <span>{t('clients')}</span>
            </button>

            <button
              onClick={() => handleMenuClick('propriedades')}
              style={{
                padding: '15px',
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '10px',
                transition: 'all 0.3s ease',
              }}
              onMouseOver={e => {
                e.currentTarget.style.backgroundColor = '#e9ecef';
              }}
              onMouseOut={e => {
                e.currentTarget.style.backgroundColor = '#f8f9fa';
              }}
            >
              <Building2 style={{ fontSize: '24px', color: '#10b981' }} />
              <span>{t('properties')}</span>
            </button>

            <button
              onClick={() => handleMenuClick('agendamentos')}
              style={{
                padding: '15px',
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '10px',
                transition: 'all 0.3s ease',
              }}
              onMouseOver={e => {
                e.currentTarget.style.backgroundColor = '#e9ecef';
              }}
              onMouseOut={e => {
                e.currentTarget.style.backgroundColor = '#f8f9fa';
              }}
            >
              <Calendar style={{ fontSize: '24px', color: '#f59e0b' }} />
              <span>{t('appointments')}</span>
            </button>

            <button
              onClick={() => handleMenuClick('financeiro')}
              style={{
                padding: '15px',
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '10px',
                transition: 'all 0.3s ease',
              }}
              onMouseOver={e => {
                e.currentTarget.style.backgroundColor = '#e9ecef';
              }}
              onMouseOut={e => {
                e.currentTarget.style.backgroundColor = '#f8f9fa';
              }}
            >
              <CreditCard style={{ fontSize: '24px', color: '#8b5cf6' }} />
              <span>{t('financial')}</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderPage = () => {
    switch (activePage) {
      case 'usuarios':
        return canAccess('usuarios') ? <UsersManagement /> : renderDashboard();
      case 'empresas':
        return canAccess('usuarios') ? <CompaniesManagement /> : renderDashboard();
      case 'permissoes-menu':
        return canAccess('permissoes-menu') ? <MenuPermissions /> : renderDashboard();
      case 'rbac':
        return canAccess('usuarios') ? <RBACManagement /> : renderDashboard();
      case 'limites':
        return canAccess('usuarios') ? <CompanyLimitsManagement /> : renderDashboard();
      case 'clientes':
        return canAccess('clientes') ? (
          <Clientes onAddProperty={handleAddProperty} />
        ) : (
          renderDashboard()
        );
      case 'funcionarios':
        return canAccess('funcionarios') ? <Funcionarios /> : renderDashboard();
      case 'servicos':
        return canAccess('servicos') ? <Servicos /> : renderDashboard();
      case 'produtos':
        return canAccess('produtos') ? <Produtos /> : renderDashboard();
      case 'propriedades':
        return canAccess('propriedades') ? (
          <Propriedades selectedClient={selectedClientForProperty} />
        ) : (
          renderDashboard()
        );
      case 'agendamentos':
        return canAccess('agendamentos') ? <Agendamentos /> : renderDashboard();
      case 'financeiro':
        return canAccess('financeiro') && canUseFinance ? <Financeiro /> : renderDashboard();
      case 'relatorios':
        return canAccess('relatorios') && canUseReports ? <Relatorios /> : renderDashboard();
      case 'zonas':
        return canAccess('zonas') ? <Zonas /> : renderDashboard();
      case 'configuracoes':
        return canAccess('configuracoes') ? <Configuracoes /> : renderDashboard();
      case 'avaliacoes':
        // Funcionários podem ver suas próprias avaliações, admins podem ver todas
        return (user?.role === 'employee' || canAccess('funcionarios')) ? <Avaliacoes /> : renderDashboard();
      case 'preferencias-notificacoes':
        // Todos os usuários podem ver suas preferências de notificação (se feature habilitada)
        return canUseNotificationPreferences ? <PreferenciasNotificacoes /> : renderDashboard();
      case 'billing':
        return canAccess('usuarios') ? <Billing /> : renderDashboard();
      case 'ajuda':
        return <Ajuda />;
      case 'suporte':
        return <Suporte />;
      default:
        return renderDashboard();
    }
  };

  const sidebarVisible = isDesktop || sidebarOpen;
  const pageTitleKey = PAGE_TITLES[activePage];
  const pageTitle = pageTitleKey ? t(pageTitleKey) : t('dashboard');

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        width: '100%',
        maxWidth: '100vw',
        overflowX: 'hidden',
      }}
    >
      {/* Overlay for mobile — antes do sidebar no DOM */}
      {!isDesktop && sidebarOpen && (
        <div
          role="presentation"
          aria-hidden
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1000,
          }}
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <div
        className="sidebar-mobile"
        style={{
          width: `${SIDEBAR_WIDTH_PX}px`,
          maxWidth: '85vw',
          backgroundColor: 'white',
          boxShadow: '2px 0 5px rgba(0,0,0,0.1)',
          position: 'fixed',
          top: 0,
          left: sidebarVisible ? 0 : `-${SIDEBAR_WIDTH_PX}px`,
          height: '100dvh',
          transition: 'left 0.3s ease',
          zIndex: 1001,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{ padding: '20px', borderBottom: '1px solid #eee', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flex: 1 }}>
            {brandLogo ? (
              <img
                src={brandLogo}
                alt="Logo da empresa"
                style={{ height: '32px', objectFit: 'contain' }}
                onError={e => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : (
              <Home style={{ color: brandPrimary, fontSize: '24px' }} />
            )}
            <h2 style={{ margin: 0, color: '#333', fontSize: '18px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>House Keep</h2>
          </div>
          {!isDesktop && (
            <button
              type="button"
              onClick={closeSidebar}
              aria-label="Fechar menu"
              data-testid="sidebar-close"
              style={{
                background: 'transparent',
                border: 'none',
                padding: 8,
                cursor: 'pointer',
                color: '#666',
                flexShrink: 0,
              }}
            >
              <X size={22} />
            </button>
          )}
          </div>
        </div>

        {/* Menu - Scrollable */}
        <nav
          data-testid="sidebar-nav"
          style={{
          padding: '20px 0',
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          minHeight: 0,
        }}>
          <MenuItem
            menuId="dashboard"
            icon={Home}
            label={t('dashboard')}
            active={activePage === 'dashboard'}
            onClick={() => handleMenuClick('dashboard')}
            accentColor={brandPrimary}
          />
          {/* Menu Admin - Usuários */}
          {canAccess('usuarios') && (
            <MenuItem
              menuId="usuarios"
              icon={Users}
              label={t('users')}
              active={activePage === 'usuarios'}
              onClick={() => handleMenuClick('usuarios')}
              accentColor={brandPrimary}
            />
          )}

          {/* Menu Admin - Empresas */}
          {canAccess('usuarios') && (
            <MenuItem
              menuId="empresas"
              icon={Building2}
              label={t('menu.companies')}
              active={activePage === 'empresas'}
              onClick={() => handleMenuClick('empresas')}
              accentColor={brandPrimary}
            />
          )}

          {/* Menu Admin - Permissões de Menu */}
          {canAccess('permissoes-menu') && (
            <MenuItem
              menuId="permissoes-menu"
              icon={Settings}
              label={t('menuPermissions')}
              active={activePage === 'permissoes-menu'}
              onClick={() => handleMenuClick('permissoes-menu')}
              accentColor={brandPrimary}
            />
          )}

          {/* Menu Admin - RBAC (Permissões Granulares) */}
          {canAccess('usuarios') && (
            <MenuItem
              menuId="rbac"
              icon={Shield}
              label={t('menu.rbac')}
              active={activePage === 'rbac'}
              onClick={() => handleMenuClick('rbac')}
              accentColor={brandPrimary}
            />
          )}

          {/* Menu Admin - Limites de Recursos */}
          {canAccess('usuarios') && (
            <MenuItem
              menuId="limites"
              icon={Settings}
              label={t('menu.limits')}
              active={activePage === 'limites'}
              onClick={() => handleMenuClick('limites')}
              accentColor={brandPrimary}
            />
          )}

          {/* Menu Admin - Faturamento SaaS (planos e faturas da plataforma) */}
          {canAccess('usuarios') && (
            <MenuItem
              menuId="billing"
              icon={TrendingUp}
              label={t('billing.title')}
              active={activePage === 'billing'}
              onClick={() => handleMenuClick('billing')}
              accentColor={brandPrimary}
            />
          )}

          {/* Menu disponível para Admin e Supervisor */}
          {canAccess('clientes') && (
            <MenuItem
              menuId="clientes"
              icon={Users}
              label={t('clients')}
              active={activePage === 'clientes'}
              onClick={() => handleMenuClick('clientes')}
              accentColor={brandPrimary}
            />
          )}

          {canAccess('funcionarios') && (
            <MenuItem
              menuId="funcionarios"
              icon={Briefcase}
              label={t('employees')}
              active={activePage === 'funcionarios'}
              onClick={() => handleMenuClick('funcionarios')}
              accentColor={brandPrimary}
            />
          )}

          {/* Avaliações - Funcionários podem ver suas próprias, admins podem ver todas */}
          {(user?.role === 'employee' || canAccess('funcionarios')) && (
            <MenuItem
              menuId="avaliacoes"
              icon={Star}
              label={t('menu.history')}
              active={activePage === 'avaliacoes'}
              onClick={() => handleMenuClick('avaliacoes')}
              accentColor={brandPrimary}
            />
          )}

          {canAccess('zonas') && (
            <MenuItem
              menuId="zonas"
              icon={MapPin}
              label={t('menu.zones')}
              active={activePage === 'zonas'}
              onClick={() => handleMenuClick('zonas')}
              accentColor={brandPrimary}
            />
          )}

          {canAccess('servicos') && (
            <MenuItem
              menuId="servicos"
              icon={Sparkles}
              label={t('menu.services')}
              active={activePage === 'servicos'}
              onClick={() => handleMenuClick('servicos')}
              accentColor={brandPrimary}
            />
          )}

          {canAccess('produtos') && (
            <MenuItem
              menuId="produtos"
              icon={Package}
              label={t('products')}
              active={activePage === 'produtos'}
              onClick={() => handleMenuClick('produtos')}
              accentColor={brandPrimary}
            />
          )}

          {canAccess('propriedades') && (
            <MenuItem
              menuId="propriedades"
              icon={Building2}
              label={t('properties')}
              active={activePage === 'propriedades'}
              onClick={() => handleMenuClick('propriedades')}
              accentColor={brandPrimary}
            />
          )}

          {/* Agendamentos - todos podem ver */}
          {canAccess('agendamentos') && (
            <MenuItem
              menuId="agendamentos"
              icon={Calendar}
              label={t('appointments')}
              active={activePage === 'agendamentos'}
              onClick={() => handleMenuClick('agendamentos')}
              accentColor={brandPrimary}
            />
          )}

          {/* Financeiro - apenas Admin */}
          {canAccess('financeiro') && canUseFinance && (
            <MenuItem
              menuId="financeiro"
              icon={CreditCard}
              label={t('financial')}
              active={activePage === 'financeiro'}
              onClick={() => handleMenuClick('financeiro')}
              accentColor={brandPrimary}
            />
          )}

          {/* Reports - Admin e Supervisor */}
          {canAccess('relatorios') && canUseReports && (
            <MenuItem
              menuId="relatorios"
              icon={Bell}
              label={t('reports')}
              active={activePage === 'relatorios'}
              onClick={() => handleMenuClick('relatorios')}
              accentColor={brandPrimary}
            />
          )}

          {canAccess('configuracoes') && (
            <MenuItem
              menuId="configuracoes"
              icon={User}
              label={t('settings')}
              active={activePage === 'configuracoes'}
              onClick={() => handleMenuClick('configuracoes')}
              accentColor={brandPrimary}
            />
          )}

          {/* Preferências de Notificação - todos os usuários */}
          {canUseNotificationPreferences && (
            <MenuItem
              menuId="preferencias-notificacoes"
              icon={Bell}
              label={t('menu.notificationPreferences')}
              active={activePage === 'preferencias-notificacoes'}
              onClick={() => handleMenuClick('preferencias-notificacoes')}
              accentColor={brandPrimary}
            />
          )}

          <MenuItem
            menuId="suporte"
            icon={Headphones}
            label={t('support.title')}
            active={activePage === 'suporte'}
            onClick={() => handleMenuClick('suporte')}
            accentColor={brandPrimary}
          />

          <MenuItem
            menuId="ajuda"
            icon={HelpCircle}
            label={t('help')}
            active={activePage === 'ajuda'}
            onClick={() => handleMenuClick('ajuda')}
            accentColor={brandPrimary}
          />
        </nav>

        {/* User Info - Fixed at bottom */}
        <div
          style={{
            padding: '15px 20px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            margin: '0 20px 20px 20px',
            flexShrink: 0,
            borderTop: '1px solid #eee',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
            <User style={{ color: '#666' }} />
            <div>
              <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                {user?.name || user?.email}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                {user?.role === 'admin' && `👑 ${t('administrator')}`}
                {user?.role === 'supervisor' && `📋 ${t('supervisor')}`}
                {user?.role === 'employee' && `👷 ${t('employee')}`}
                {user?.role === 'client' && `👤 ${t('clientRole')}`}
              </div>
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-button"
            style={{
              width: '100%',
              padding: '8px',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              justifyContent: 'center',
            }}
          >
            <LogOut style={{ fontSize: '16px' }} />
            {t('logout')}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div
        style={{
          marginLeft: isDesktop ? `${SIDEBAR_WIDTH_PX}px` : 0,
          width: isDesktop ? `calc(100% - ${SIDEBAR_WIDTH_PX}px)` : '100%',
          maxWidth: '100%',
          minWidth: 0,
          transition: 'margin-left 0.3s ease',
        }}
      >
        {/* Header */}
        <header
          data-testid="dashboard-header"
          style={{
            backgroundColor: 'white',
            padding: isDesktop ? '15px 30px' : '12px 16px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0, flex: '1 1 auto' }}>
            {!isDesktop && (
              <button
                type="button"
                onClick={() => (sidebarOpen ? closeSidebar() : setSidebarOpen(true))}
                data-testid="sidebar-toggle"
                aria-label={sidebarOpen ? 'Fechar menu' : 'Abrir menu'}
                aria-expanded={sidebarOpen}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: 8,
                  flexShrink: 0,
                }}
              >
                {sidebarOpen ? <ChevronLeft size={22} /> : <Menu size={22} />}
              </button>
            )}
            <h1
              style={{
                margin: 0,
                color: '#333',
                fontSize: isDesktop ? '1.5rem' : '1.125rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {pageTitle}
            </h1>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
              flex: '0 1 auto',
              maxWidth: '100%',
            }}
          >
            <CompanySelector />
            <GlobalSearch onResultClick={handleSearchResultClick} openTrigger={searchOpenTriggerRef.current} />
            <OfflineStatusBanner />
            <DemoModeToggle enabled={demoModeEnabled} onToggle={handleToggleDemoMode} />
            <button
              onClick={openChat}
              style={{
                backgroundColor: '#2563eb',
                border: 'none',
                borderRadius: '9999px',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 14px',
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              <MessageCircle style={{ fontSize: '18px' }} />
              Chat
            </button>
            <Bell style={{ fontSize: '20px', color: '#666', cursor: 'pointer' }} />
          </div>
        </header>

        {/* Content */}
        <main
          data-testid="page-content"
          style={{ padding: 0, width: '100%', maxWidth: '100%', overflowX: 'auto' }}
        >
          {renderPage()}
        </main>
      </div>
      <ChatModal isOpen={isChatOpen} onClose={closeChat} />
      <KeyboardShortcutsModal
        isOpen={showShortcutsModal}
        onClose={() => setShowShortcutsModal(false)}
      />
    </div>
  );
};

const DemoModeToggle: React.FC<{ enabled: boolean; onToggle: () => void }> = ({ enabled, onToggle }) => (
  <button
    onClick={onToggle}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '8px 14px',
      borderRadius: '9999px',
      border: 'none',
      cursor: 'pointer',
      backgroundColor: enabled ? '#14b8a6' : '#e5e7eb',
      color: enabled ? 'white' : '#374151',
      fontWeight: 600,
      transition: 'all 0.2s ease',
    }}
  >
    <Sparkles size={16} />
    {enabled ? 'Modo demo ligado' : 'Modo demo desligado'}
  </button>
);

const DemoBanner: React.FC<{
  title: string;
  description: string;
  actionLabel: string;
  onAction: () => void;
  variant?: 'cta' | 'active';
}> = ({ title, description, actionLabel, onAction, variant = 'cta' }) => {
  const colors =
    variant === 'active'
      ? { bg: '#ecfeff', border: '#06b6d4', text: '#0f172a' }
      : { bg: '#fef3c7', border: '#f59e0b', text: '#92400e' };

  return (
    <div
      style={{
        backgroundColor: colors.bg,
        border: `1px solid ${colors.border}`,
        color: colors.text,
        borderRadius: '10px',
        padding: '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '20px',
        flexWrap: 'wrap',
      }}
    >
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, fontSize: '16px', marginBottom: '6px' }}>{title}</div>
        <div style={{ fontSize: '14px', lineHeight: 1.5 }}>{description}</div>
      </div>
      <button
        onClick={onAction}
        style={{
          backgroundColor: colors.border,
          color: variant === 'active' ? 'white' : '#1f2933',
          border: 'none',
          padding: '10px 18px',
          borderRadius: '8px',
          cursor: 'pointer',
          fontWeight: 600,
        }}
      >
        {actionLabel}
      </button>
    </div>
  );
};

// Componente para item do menu
const MenuItem: React.FC<{
  menuId?: string;
  icon: React.ComponentType<{ style?: React.CSSProperties }>;
  label: string;
  active?: boolean;
  onClick: () => void;
  accentColor?: string;
}> = ({ menuId, icon: Icon, label, active, onClick, accentColor = '#9333ea' }) => (
  <button
    type="button"
    onClick={onClick}
    className={active ? 'active' : ''}
    aria-current={active ? 'page' : undefined}
    data-testid={menuId ? `menu-${menuId}` : undefined}
    style={{
      width: '100%',
      padding: '12px 20px',
      border: 'none',
      backgroundColor: active ? accentColor : 'transparent',
      color: active ? 'white' : '#333',
      textAlign: 'left',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      transition: 'background-color 0.2s',
      minHeight: 44,
    }}
    onMouseOver={e => !active && (e.currentTarget.style.backgroundColor = accentColor + '15')}
    onMouseOut={e => !active && (e.currentTarget.style.backgroundColor = 'transparent')}
  >
    <Icon style={{ fontSize: '18px', color: active ? 'white' : accentColor, flexShrink: 0 }} />
    <span
      style={{
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        lineHeight: 1.3,
      }}
    >
      {label}
    </span>
  </button>
);

// Componente para card de estatística
const StatCard: React.FC<{ icon: any; label: string; value: string | number; color: string }> = ({
  icon: Icon,
  label,
  value,
  color,
}) => (
  <div
    style={{
      backgroundColor: 'white',
      padding: '20px',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
    }}
  >
    <div
      style={{
        backgroundColor: color + '20',
        padding: '12px',
        borderRadius: '8px',
      }}
    >
      <Icon style={{ color, fontSize: '24px' }} />
    </div>
    <div>
      <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#333' }}>{value}</div>
      <div style={{ fontSize: '14px', color: '#666' }}>{label}</div>
    </div>
  </div>
);

export default SimpleDashboard;
